import Navbar from '@/component/navbar';
import { showAlert } from '@/components/AppDialog';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getEnv } from '@/utils/env';
import { useTranslation } from 'react-i18next';

import { API_BASE_URL } from '@/utils/env';

export default function Raise_capital() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    raiseAmount: '',
    roundType: '',
    capitalType: '',
    targetValuation: '',
    companyInfo: '',
    website: ''
  });

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    const checkExistingSubmission = async () => {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        try {
          const { API_BASE_URL } = await import('@/utils/env');
          const { getToken } = await import('@/utils/auth');
          const token = getToken();

          const response = await fetch(`${API_BASE_URL}/auth/user/${user.uid}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (response.ok) {
            const data = await response.json();
            if (data.success && data.user && data.user.raiseCapitalForm) {
              console.log('User already raised capital, redirecting to schedule...');
              // Pass necessary data if needed, or just redirect
              navigate('/raising/schedule', {
                state: {
                  demoData: {
                    firstName: data.user.firstName,
                    lastName: data.user.lastName,
                    email: data.user.email,
                    companyName: data.user.raiseCapitalForm.companyInfo
                  }
                }
              });
            }
          }
        } catch (error) {
          console.error('Error checking existing submission:', error);
        }
      }
    };

    checkExistingSubmission();
  }, [navigate]);

  const handleContinue = async () => {
    setLoading(true);
    try {
      // Validate required fields
      if (!formData.raiseAmount || !formData.roundType || !formData.capitalType || !formData.companyInfo || !formData.website) {
        showAlert(t('raiseCapital.alert.fillRequired'));
        setLoading(false);
        return;
      }

      // Get user from localStorage
      const userStr = localStorage.getItem('user');
      if (!userStr) {
        showAlert(t('raiseCapital.alert.loginFirst'));
        navigate('/login');
        return;
      }

      const user = JSON.parse(userStr);

      console.log('💰 Raise Capital Form Data:', formData);

      // Save raise capital data via backend
      const tokensStr = localStorage.getItem('authTokens');
      const idToken = tokensStr ? JSON.parse(tokensStr).idToken : null;

      const response = await fetch(`${API_BASE_URL}/auth/user/${user.uid}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(idToken ? { Authorization: `Bearer ${idToken}` } : {})
        },
        body: JSON.stringify({
          raiseCapitalForm: {
            ...formData,
            completedAt: new Date().toISOString()
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save raise capital form');
      }

      console.log('✅ Raise capital form saved successfully');

      // Navigate to pricing page
      navigate('/pricing2');
    } catch (error) {
      console.error('Error saving raise capital form:', error);
      showAlert(t('raiseCapital.alert.saveFailed'));
    } finally {
      setLoading(false);
    }
  };

  const RadioButton = ({
    checked,
    label,
    onClick
  }: {
    checked: boolean;
    label: string;
    onClick: () => void
  }) => (
    <button
      onClick={onClick}
      className={`px-4 py-3 rounded-lg border-2 text-left transition-all ${checked
        ? 'border-blue-600 bg-blue-50'
        : 'border-gray-300 bg-white hover:border-gray-400'
        }`}
    >
      <div className="flex items-center gap-2">
        <div
          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${checked ? 'border-blue-600' : 'border-gray-300'
            }`}
        >
          {checked && (
            <div className="w-3 h-3 rounded-full bg-blue-600"></div>
          )}
        </div>
        <span className="text-sm font-medium text-gray-900">{label}</span>
      </div>
    </button>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar hideSignBtn />

      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('raiseCapital.title')}</h1>
          <p className="text-gray-600">{t('raiseCapital.subtitle')}</p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-xl shadow-sm p-8 space-y-8">
          {/* Question 1: How much are you looking to raise? */}
          <div>
            <label className="block mb-2">
              <span className="text-lg font-bold text-gray-900">{t('raiseCapital.q1.title')}</span>
              <p className="text-sm text-gray-600 mt-1">{t('raiseCapital.q1.subtitle')}</p>
            </label>
            <input
              type="text"
              placeholder={t('raiseCapital.q1.placeholder')}
              value={formData.raiseAmount}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9]/g, '');
                handleInputChange('raiseAmount', val);
              }}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600 bg-white text-gray-900"
            />
          </div>

          {/* Question 2: What is the Round that you are raising for? */}
          <div>
            <label className="block mb-4">
              <span className="text-lg font-bold text-gray-900">{t('raiseCapital.q2.title')}</span>
              <p className="text-sm text-gray-600 mt-1">{t('raiseCapital.q2.subtitle')}</p>
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <RadioButton
                checked={formData.roundType === 'pre-seed'}
                label={t('raiseCapital.q2.preSeed')}
                onClick={() => handleInputChange('roundType', 'pre-seed')}
              />
              <RadioButton
                checked={formData.roundType === 'seed'}
                label={t('raiseCapital.q2.seed')}
                onClick={() => handleInputChange('roundType', 'seed')}
              />
              <RadioButton
                checked={formData.roundType === 'series-a'}
                label={t('raiseCapital.q2.seriesA')}
                onClick={() => handleInputChange('roundType', 'series-a')}
              />
              <RadioButton
                checked={formData.roundType === 'series-b'}
                label={t('raiseCapital.q2.seriesB')}
                onClick={() => handleInputChange('roundType', 'series-b')}
              />
              <RadioButton
                checked={formData.roundType === 'not-sure-yet'}
                label={t('raiseCapital.q2.notSure')}
                onClick={() => handleInputChange('roundType', 'not-sure-yet')}
              />
            </div>
          </div>

          {/* Question 3: What type of capital are you raising? */}
          <div>
            <label className="block mb-4">
              <span className="text-lg font-bold text-gray-900">{t('raiseCapital.q3.title')}</span>
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <RadioButton
                checked={formData.capitalType === 'equity'}
                label={t('raiseCapital.q3.equity')}
                onClick={() => handleInputChange('capitalType', 'equity')}
              />
              <RadioButton
                checked={formData.capitalType === 'convertible-safe'}
                label={t('raiseCapital.q3.convertible')}
                onClick={() => handleInputChange('capitalType', 'convertible-safe')}
              />
              <RadioButton
                checked={formData.capitalType === 'debt'}
                label={t('raiseCapital.q3.debt')}
                onClick={() => handleInputChange('capitalType', 'debt')}
              />
              <RadioButton
                checked={formData.capitalType === 'not-sure-yet'}
                label={t('raiseCapital.q3.notSure')}
                onClick={() => handleInputChange('capitalType', 'not-sure-yet')}
              />
            </div>
          </div>

          {/* Question 4: Do you have a target valuation or cap? (optional) */}
          <div>
            <label className="block mb-2">
              <span className="text-lg font-bold text-gray-900">{t('raiseCapital.q4.title')}</span>
              <p className="text-sm text-gray-600 mt-1">{t('raiseCapital.q4.subtitle')}</p>
            </label>
            <input
              type="text"
              placeholder={t('raiseCapital.q4.placeholder')}
              value={formData.targetValuation}
              onChange={(e) => handleInputChange('targetValuation', e.target.value.replace(/[^0-9]/g, ''))}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600 bg-white text-gray-900"
            />
          </div>

          {/* Question 5: Tell us about your company */}
          <div>
            <label className="block mb-2">
              <span className="text-lg font-bold text-gray-900">{t('raiseCapital.q5.title')}</span>
              <p className="text-sm text-gray-600 mt-1">{t('raiseCapital.q5.subtitle')}</p>
            </label>
            <textarea
              placeholder={t('raiseCapital.q5.placeholder')}
              value={formData.companyInfo}
              onChange={(e) => handleInputChange('companyInfo', e.target.value)}
              rows={6}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600 bg-white text-gray-900 resize-none"
            />
          </div>

          {/* Question 6: Website */}
          <div>
            <input
              type="url"
              placeholder={t('raiseCapital.q6.placeholder')}
              value={formData.website}
              onChange={(e) => handleInputChange('website', e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600 bg-white text-gray-900"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-4 pt-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="px-6 py-3 border-2 border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
            >
              {t('raiseCapital.backToDashboard')}
            </button>
            <button
              onClick={handleContinue}
              disabled={loading}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {loading ? t('raiseCapital.saving') : t('raiseCapital.continue')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
